import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, ShoppingBag, ShieldCheck, Gamepad2, Camera, CameraOff, FileDown, Download, ListChecks, Users, CalendarRange, ClipboardList, Briefcase, CalendarDays } from "lucide-react";
import { AdminPrompts } from "@/pages/admin/AdminPrompts";
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { AdminGames } from "@/pages/admin/AdminGames";
import { AdminCurriculum } from "@/pages/admin/AdminCurriculum";
import { AdminToolbox } from "@/pages/admin/AdminToolbox";
import { AdminEvents } from "@/pages/admin/AdminEvents";

const GAMES_TEMPLATE = `name,description,example,skill_sets,non_speaking_modification,level,tags
Sample Game,What the game is and how it works,How a single round looks in play,"Focus, Listening, Teamwork",How to include non-speaking or AAC players,1,"warm-up, circle"
Another Game,A short description,An example round,"Quick Thinking, Creative Thinking",Non-speaking modification here,2,"game"
`;

const PROMPTS_TEMPLATE = `category,level,prompt,paired_value
Occupations,1,Astronaut,Their helmet is fogging up
Occupations,1,Chef,The oven won't turn off
Locations,1,A crowded elevator,
Emotions,1,Overjoyed,
`;
import { toast } from "sonner";

export default function Admin() {
  const [orders, setOrders] = useState([]);
  const [consents, setConsents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importCatResult, setImportCatResult] = useState(null);
  const fileRef = useRef(null);
  const catFileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/consent-log").then(({ data }) => setConsents(data)).catch(() => {});
    api.get("/admin/registrations").then(({ data }) => setRegistrations(data)).catch(() => {});
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/import/games", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadCategories = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/import/categories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportCatResult(data);
      toast.success(`Imported ${data.prompts_imported} prompts across ${data.categories_created + data.categories_updated} categories`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (catFileRef.current) catFileRef.current.value = "";
    }
  };

  const downloadTemplate = (filename, content) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadConsentPdf = async (id) => {
    try {
      const res = await api.get(`/admin/consent/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consent_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the consent PDF");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Staff Admin</span>
      <h1 className="mt-2 font-display text-4xl text-[#550001]">Agenda Builder &amp; operations</h1>

      <Tabs defaultValue="games" className="mt-8">
        <TabsList className="bg-white border border-[#dfd8da] flex-wrap h-auto">
          <TabsTrigger value="games" data-testid="admin-tab-games"><Gamepad2 size={15} className="mr-1" /> Games</TabsTrigger>
          <TabsTrigger value="import" data-testid="admin-tab-import"><FileSpreadsheet size={15} className="mr-1" /> Bulk Import</TabsTrigger>
          <TabsTrigger value="prompts" data-testid="admin-tab-prompts"><ListChecks size={15} className="mr-1" /> Prompts</TabsTrigger>
          <TabsTrigger value="curriculum" data-testid="admin-tab-curriculum"><CalendarRange size={15} className="mr-1" /> Agenda Builder</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders"><ShoppingBag size={15} className="mr-1" /> Orders</TabsTrigger>
          <TabsTrigger value="consent" data-testid="admin-tab-consent"><ShieldCheck size={15} className="mr-1" /> Consent Log</TabsTrigger>
          <TabsTrigger value="registrations" data-testid="admin-tab-registrations"><ClipboardList size={15} className="mr-1" /> Registrations</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={15} className="mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="toolbox" data-testid="admin-tab-toolbox"><Briefcase size={15} className="mr-1" /> Tool Box</TabsTrigger>
          <TabsTrigger value="events" data-testid="admin-tab-events"><CalendarDays size={15} className="mr-1" /> Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="toolbox" className="mt-6"><AdminToolbox /></TabsContent>
        <TabsContent value="events" className="mt-6"><AdminEvents /></TabsContent>

        <TabsContent value="games" className="mt-6">
          <AdminGames />
        </TabsContent>

        <TabsContent value="import" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-[#dfd8da] p-8">
              <h2 className="text-xl font-bold text-[#1a1617]">Bulk import games</h2>
              <p className="mt-2 text-sm text-[#545454]">Upload a CSV or XLSX. Columns: <code className="bg-[#f9f6f6] px-1 rounded">name, description, example, skill_sets, non_speaking_modification, level, tags</code>. Multi-value fields are comma-separated. Existing games (by name) are updated.</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={upload} className="hidden" id="import-file" data-testid="admin-import-input" />
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="admin-import-btn">
                  <label htmlFor="import-file" className="cursor-pointer"><Upload size={16} className="mr-2" /> Choose games file</label>
                </Button>
                <Button variant="outline" onClick={() => downloadTemplate("games_template.csv", GAMES_TEMPLATE)} data-testid="games-template-btn" className="border-[#920e12] text-[#920e12]">
                  <Download size={16} className="mr-2" /> Template
                </Button>
              </div>
              {importResult && (
                <div className="mt-5 bg-[#dcfce7] text-[#15803d] rounded-lg p-4 font-semibold" data-testid="admin-import-result">
                  Processed {importResult.total_rows} rows · {importResult.created} created · {importResult.updated} updated.
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-[#dfd8da] p-8">
              <h2 className="text-xl font-bold text-[#1a1617]">Bulk import categories &amp; prompts</h2>
              <p className="mt-2 text-sm text-[#545454]">Upload a CSV or XLSX with one row per prompt. Columns: <code className="bg-[#f9f6f6] px-1 rounded">category, level, prompt, paired_value</code>. Use <code className="bg-[#f9f6f6] px-1 rounded">paired_value</code> for Occupation → Problem pairings (e.g. prompt "Astronaut", paired_value "Their helmet is fogging up"). Rows are grouped by category + level; existing categories are replaced.</p>
              <input ref={catFileRef} type="file" accept=".csv,.xlsx,.xls" onChange={uploadCategories} className="hidden" id="import-cat-file" data-testid="admin-import-cat-input" />
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="admin-import-cat-btn">
                  <label htmlFor="import-cat-file" className="cursor-pointer"><Upload size={16} className="mr-2" /> Choose prompts file</label>
                </Button>
                <Button variant="outline" onClick={() => downloadTemplate("prompts_template.csv", PROMPTS_TEMPLATE)} data-testid="prompts-template-btn" className="border-[#920e12] text-[#920e12]">
                  <Download size={16} className="mr-2" /> Template
                </Button>
              </div>
              {importCatResult && (
                <div className="mt-5 bg-[#dcfce7] text-[#15803d] rounded-lg p-4 font-semibold" data-testid="admin-import-cat-result">
                  {importCatResult.prompts_imported} prompts · {importCatResult.categories_created} categories created · {importCatResult.categories_updated} updated. Review them under <strong>Prompts</strong> before publishing.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="prompts" className="mt-6">
          <AdminPrompts />
        </TabsContent>

        <TabsContent value="curriculum" className="mt-6">
          <AdminCurriculum />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <AdminUsers />
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="overflow-x-auto bg-white rounded-2xl border border-[#dfd8da]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#f9f6f6] text-[#545454] uppercase text-xs tracking-wide">
                <tr><th className="p-3">Product</th><th className="p-3">Amount</th><th className="p-3">Buyer</th><th className="p-3">Date</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-[#dfd8da]" data-testid={`admin-order-${o.id}`}>
                    <td className="p-3 font-bold text-[#1a1617]">{o.product}</td>
                    <td className="p-3">${o.amount.toFixed(2)} {o.currency.toUpperCase()}</td>
                    <td className="p-3 text-[#545454]">{o.buyer || "—"}</td>
                    <td className="p-3 text-[#545454]">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && <p className="p-6 text-[#545454]">No orders yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="consent" className="mt-6">
          <div className="overflow-x-auto bg-white rounded-2xl border border-[#dfd8da]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#f9f6f6] text-[#545454] uppercase text-xs tracking-wide">
                <tr><th className="p-3">Participant</th><th className="p-3">Type</th><th className="p-3">Status</th><th className="p-3">Version</th><th className="p-3">When</th><th className="p-3">By</th><th className="p-3">Record</th></tr>
              </thead>
              <tbody>
                {consents.map((c) => (
                  <tr key={c.id} className="border-t border-[#dfd8da]" data-testid={`admin-consent-${c.id}`}>
                    <td className="p-3 font-bold text-[#1a1617]">{c.participant}</td>
                    <td className="p-3 text-[#545454] capitalize">{c.type.replace(/_/g, " ")}</td>
                    <td className="p-3">
                      {c.granted
                        ? <span className="flex items-center gap-1 text-[#15803d] font-semibold"><Camera size={14} /> Granted</span>
                        : <span className="flex items-center gap-1 text-[#991b1b] font-semibold"><CameraOff size={14} /> Denied/Revoked</span>}
                    </td>
                    <td className="p-3">{c.version}</td>
                    <td className="p-3 text-[#545454]">{new Date(c.granted_at).toLocaleString()}</td>
                    <td className="p-3 text-[#545454]">{c.granted_by}</td>
                    <td className="p-3">
                      <button onClick={() => downloadConsentPdf(c.id)} data-testid={`consent-pdf-${c.id}`}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-[#920e12] hover:underline">
                        <FileDown size={15} /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {consents.length === 0 && <p className="p-6 text-[#545454]">No consent records yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="registrations" className="mt-6">
          <div className="overflow-x-auto bg-white rounded-2xl border border-[#dfd8da]" data-testid="admin-registrations">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#f9f6f6] text-[#545454] uppercase text-xs tracking-wide">
                <tr>
                  <th className="p-3">Participant</th><th className="p-3">DOB</th><th className="p-3">Guardian</th>
                  <th className="p-3">Guardian contact</th><th className="p-3">Billing</th><th className="p-3">Releases</th><th className="p-3">New/Ret.</th>
                  <th className="p-3">Emergency</th><th className="p-3">Goals</th><th className="p-3">When</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r) => (
                  <tr key={r.id} className="border-t border-[#dfd8da] align-top" data-testid={`admin-registration-${r.id}`}>
                    <td className="p-3 font-bold text-[#1a1617]">{r.participant_name}<div className="font-normal text-xs text-[#545454]">{r.participant_email}</div><div className="font-normal text-xs text-[#545454]">{r.address}</div></td>
                    <td className="p-3 text-[#545454]">{r.date_of_birth}</td>
                    <td className="p-3 text-[#545454]">{r.guardian_name}<div className="text-xs">{r.guardian_relation}</div></td>
                    <td className="p-3 text-[#545454]">{r.guardian_email}<div className="text-xs">{r.guardian_phone}</div></td>
                    <td className="p-3 text-[#545454]">{r.billing_party}</td>
                    <td className="p-3 text-xs">
                      <div className={r.photo_release ? "text-[#15803d] font-semibold" : "text-[#991b1b]"}>{r.photo_release ? "✓" : "✗"} Photo/Video</div>
                      <div className={r.liability_release ? "text-[#15803d] font-semibold" : "text-[#991b1b]"}>{r.liability_release ? "✓" : "✗"} Liability</div>
                    </td>
                    <td className="p-3 text-[#545454]">{r.participant_status || "—"}</td>
                    <td className="p-3 text-[#545454] max-w-[12rem]">{r.emergency_contact}</td>
                    <td className="p-3 text-[#545454] max-w-[14rem]">{r.goals || "—"}{r.support_notes ? <div className="mt-1 text-xs italic">Support: {r.support_notes}</div> : null}{r.comments ? <div className="mt-1 text-xs italic">Note: {r.comments}</div> : null}</td>
                    <td className="p-3 text-[#545454] whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {registrations.length === 0 && <p className="p-6 text-[#545454]">No class registrations yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
